import React from "react";
import { CartEventPackageItem } from "../../models/Cart";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import CartStore from "../../stores/CartStore";
import { capitalizeWords, priceFormatter } from "../../utils/helpers";
import TrashIcon from "@mui/icons-material/DeleteOutline";
import { EventPackage } from "../../models/AssignedSeating";
import { removeCartSeatWithStrandGuard } from "../seatmap/cartStrandGuard";

type Props = {
    row: {
        eventPackage: EventPackage;
        items: CartEventPackageItem[];
    };
    cartStore: CartStore | null;
};

const SeatRow = ({
    item,
    cartStore,
}: {
    item: CartEventPackageItem;
    cartStore: CartStore | null;
}) => {
    const getPriceNode = (): React.ReactNode => {
        if (item.comp) {
            return <Chip color="warning" label="COMP" size="small" />;
        }
        if (item.price === 0) {
            return <Chip color="success" label="FREE" size="small" />;
        }
        return (
            <Typography variant="body2">
                {priceFormatter(item.price)}
            </Typography>
        );
    };

    const getDiscountNode = (): React.ReactNode => {
        if (item.comp) return null;
        if (item.discount) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(
                        item.discount.grossAmount ?? item.discount.amount,
                    )}{" "}
                    in discounts
                </Typography>
            );
        }
        return null;
    };

    const getServiceFeeNode = (): React.ReactNode => {
        if (item.comp) return null;
        return item.serviceFees.map((it) => (
            <Typography variant="body2" color="text.secondary" key={it.id}>
                + {priceFormatter(it.amount)} {it.name}
            </Typography>
        ));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primaryText.main"
                    >
                        {item.eventPackagePriceGroup.name}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {capitalizeWords(item.seat?.seatDetails ?? "")}
                    </Typography>
                    {getPriceNode()}
                    {getServiceFeeNode()}
                    {getDiscountNode()}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        color="error"
                        onClick={() => {
                            if (!cartStore) return;
                            const eventPackageId = item.eventPackage.id;
                            const seatId = item.seat?.id;
                            if (seatId == null) {
                                cartStore.removeEventPackageFromCart(
                                    item.id,
                                    eventPackageId,
                                    0,
                                );
                                return;
                            }
                            // Handing a seat back can strand its neighbour,
                            // so the same guard the seatmap applies runs here
                            // too.
                            void removeCartSeatWithStrandGuard({
                                cartStore,
                                source: { kind: "package", eventPackageId },
                                seatId,
                                remove: async (id) => {
                                    const target =
                                        cartStore.packageItems.find(
                                            (it) =>
                                                it.eventPackage.id ===
                                                    eventPackageId &&
                                                it.seatId === id,
                                        );
                                    if (!target) return;
                                    await cartStore.removeEventPackageFromCart(
                                        target.id,
                                        eventPackageId,
                                        id,
                                    );
                                },
                            });
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="body2">
                        {priceFormatter(cartStore?.packageItemTotal(item) ?? 0)}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

type SectionGroup = {
    key: string;
    items: CartEventPackageItem[];
};

const SectionRow = ({
    group,
    cartStore,
}: {
    group: SectionGroup;
    cartStore: CartStore | null;
}) => {
    const sample = group.items[0];
    const quantity = group.items.length;
    const lineTotal = group.items.reduce(
        (sum, it) => sum + (cartStore?.packageItemTotal(it) ?? 0),
        0,
    );
    const unitPrice = sample.price;

    const getPriceNode = (): React.ReactNode => {
        if (sample.comp) {
            return <Chip color="warning" label="COMP" size="small" />;
        }
        if (sample.price === 0) {
            return <Chip color="success" label="FREE" size="small" />;
        }
        return (
            <Typography variant="body2">
                {priceFormatter(unitPrice)} × {quantity}
            </Typography>
        );
    };

    const getServiceFeeNode = (): React.ReactNode => {
        if (sample.comp) return null;
        // Service fees are per-item; show the per-ticket fee with a per-ticket label.
        return sample.serviceFees.map((it) => (
            <Typography variant="body2" color="text.secondary" key={it.id}>
                + {priceFormatter(it.amount)} {it.name} ea.
            </Typography>
        ));
    };

    const getDiscountNode = (): React.ReactNode => {
        if (sample.comp) return null;
        const totalDiscount = group.items.reduce(
            (sum, it) => sum + (it.discount?.amount ?? 0),
            0,
        );
        if (totalDiscount <= 0) return null;
        return (
            <Typography variant="body2" color="success.main">
                {priceFormatter(totalDiscount)} in discounts
            </Typography>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primaryText.main"
                    >
                        {sample.eventPackagePriceGroup.name}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {capitalizeWords(
                            sample.section?.name ??
                                sample.section?.sectionDetails ??
                                "",
                        )}
                    </Typography>
                    {getPriceNode()}
                    {getServiceFeeNode()}
                    {getDiscountNode()}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        color="error"
                        onClick={() => {
                            if (sample.sectionId == null) return;
                            cartStore?.setPackageSectionQtyInCart(
                                sample.eventPackage.id,
                                sample.sectionId,
                                sample.eventPackagePriceGroup.id,
                                0,
                                sample.comp,
                            );
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="body2">
                        {priceFormatter(lineTotal)}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

const PackageCartRow = ({ row, cartStore }: Props) => {
    // Split into seat-based items (one row each) and section-based items
    // (grouped by section + price group + comp).
    const seatItems: CartEventPackageItem[] = [];
    const sectionGroupMap: Record<string, CartEventPackageItem[]> = {};

    for (const it of row.items) {
        if (it.section != null && it.seat == null) {
            const key = `${it.sectionId}-${it.eventPackagePriceGroup.id}-${it.comp}`;
            if (!sectionGroupMap[key]) sectionGroupMap[key] = [];
            sectionGroupMap[key].push(it);
        } else {
            seatItems.push(it);
        }
    }

    const sectionGroups: SectionGroup[] = Object.entries(sectionGroupMap).map(
        ([key, items]) => ({ key, items }),
    );

    const sortedSeatItems = seatItems
        .slice()
        .sort((a, b) => (a.seat?.id ?? 0) - (b.seat?.id ?? 0));

    const totalRows = sortedSeatItems.length + sectionGroups.length;

    return (
        <Card>
            <CardContent>
                <Typography
                    variant="body2"
                    color="primaryText.main"
                    fontWeight={600}
                >
                    {row.eventPackage.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    {sortedSeatItems.map((it2, idx) => (
                        <Grid item xs={12} key={it2.id}>
                            <SeatRow item={it2} cartStore={cartStore} />
                            {idx !== totalRows - 1 && (
                                <Divider sx={{ mt: 2 }} />
                            )}
                        </Grid>
                    ))}
                    {sectionGroups.map((group, idx) => (
                        <Grid item xs={12} key={group.key}>
                            <SectionRow group={group} cartStore={cartStore} />
                            {sortedSeatItems.length + idx !==
                                totalRows - 1 && <Divider sx={{ mt: 2 }} />}
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default PackageCartRow;
